import { useMemo } from "react";
import { ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import MenuSection from "../components/MenuSection";
import { Reveal } from "../components/Reveal";
import { FOOD_CATEGORIES, LIMITED_TIME_TACOS } from "../data/food";
import { BASE_KEYWORDS, breadcrumbSchema, PAGE_KEYWORDS, TACOVERSE_MENU_URL, TACOVERSE_ORDER_URL } from "../data/brand";

const MENU = [FOOD_CATEGORIES[0], LIMITED_TIME_TACOS, ...FOOD_CATEGORIES.slice(1)];

export default function Food() {
  const menuSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Menu",
      name: "Food by Tacoverse — served at Barverse",
      hasMenuSection: MENU.filter((c) => c.items.length > 0).map((c) => ({
        "@type": "MenuSection",
        name: c.label,
        hasMenuItem: c.items.map((it) => ({
          "@type": "MenuItem",
          name: it.style ? `${it.name} — ${it.style}` : it.name,
        })),
      })),
    }),
    []
  );

  return (
    <div className="pt-28 md:pt-36" data-testid="food-page">
      <Seo
        title="Tex-Mex Food — Tacos, Burritos, Bowls (Food by Tacoverse)"
        description="The full Tacoverse menu, served right here in Barverse — tacos, burritos, bowls, salads, quesadillas, sides, desserts and kids options."
        path="/food"
        jsonLd={[menuSchema, breadcrumbSchema("/food", "Food")]}
        keywords={`${BASE_KEYWORDS}, ${PAGE_KEYWORDS.food}`}
      />

      <header className="container-x">
        <Reveal>
          <p className="eyebrow">
            <span className="flex items-center gap-2 text-[#B45309]">
              <img src="/images/tacosun.png" alt="" className="h-3.5 w-auto" aria-hidden="true" />
              Food by Tacoverse™
            </span>
          </p>
          <h1 className="mt-5 font-display text-5xl uppercase leading-[0.9] text-bv-ink sm:text-7xl lg:text-8xl">
            Food by
            <br />
            <img
              src="/images/tacoverse-logo.png"
              alt="Tacoverse™"
              className="mt-3 inline-block h-12 w-auto md:h-20"
            />
          </h1>
          <p className="mt-4 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-[#B45309]">
            Tex Mex Auténtico
          </p>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-bv-sub">
            The full Tacoverse menu. Right here in Barverse.
          </p>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-bv-faint">
            Served inside Barverse · Online ordering lives at tacoverse.com
          </p>
        </Reveal>
      </header>

      <div className="container-x mt-16 space-y-16 pb-16">
        {MENU.map((c, i) => (
          <MenuSection key={c.id} index={i + 1} id={c.id} title={c.label} items={c.items} note={c.note} />
        ))}
      </div>

      <div className="container-x pb-24">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl border border-bv-gold/50 bg-[#FFF6E8] p-8 md:flex-row md:items-center md:p-12">
            <div>
              <p className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#B45309]">
                <img src="/images/tacosun.png" alt="" className="h-3.5 w-auto" aria-hidden="true" />
                Online ordering lives at Tacoverse
              </p>
              <h2 className="mt-3 font-display text-3xl uppercase text-bv-ink md:text-5xl">
                View full Tacoverse menu
              </h2>
              <p className="mt-2 text-sm text-bv-sub">Menu and ordering open in this browser tab.</p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              <a href={TACOVERSE_MENU_URL} data-testid="food-tacoverse-menu-cta" className="btn-primary">
                Full Menu <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a href={TACOVERSE_ORDER_URL} data-testid="food-order-online-cta" className="btn-ghost">
                Order Online <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </Reveal>

        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.15em] text-bv-faint">
          Menu availability may vary by location. No food photography here — the menu is the
          menu.
        </p>
      </div>
    </div>
  );
}
